import { RoleType } from '../types/api';
import { SecuritySettings } from '../types/audit';

export const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

export const sanitizeInput = (value: string): string =>
  value
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=/gi, '')
    .trim()
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

export const sanitizeObject = <T extends Record<string, any>>(obj: T): T => {
  const result: Record<string, any> = Array.isArray(obj) ? [] : {};
  Object.entries(obj).forEach(([key, val]) => {
    if (typeof val === 'string') result[key] = sanitizeInput(val);
    else if (val && typeof val === 'object') result[key] = sanitizeObject(val);
    else result[key] = val;
  });
  return result as T;
};

export const isValidEmail = (email: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

export interface PasswordValidationResult {
  valid: boolean;
  errors: string[];
}

export const validatePassword = (
  password: string,
  policy: Pick<SecuritySettings,
    'password_min_length' | 'password_require_uppercase' | 'password_require_numbers' | 'password_require_special'>
): PasswordValidationResult => {
  const errors: string[] = [];
  if (password.length < policy.password_min_length) {
    errors.push(`Must be at least ${policy.password_min_length} characters long.`);
  }
  if (policy.password_require_uppercase && !/[A-Z]/.test(password)) {
    errors.push('Must contain at least one uppercase letter.');
  }
  if (policy.password_require_numbers && !/[0-9]/.test(password)) {
    errors.push('Must contain at least one number.');
  }
  if (policy.password_require_special && !/[^A-Za-z0-9]/.test(password)) {
    errors.push('Must contain at least one special character.');
  }
  return { valid: errors.length === 0, errors };
};

export type PermissionAction =
  | 'complaint:create'
  | 'complaint:read'
  | 'complaint:update'
  | 'complaint:close'
  | 'workorder:create'
  | 'workorder:read'
  | 'workorder:update'
  | 'inspection:perform'
  | 'verification:perform'
  | 'config:update'
  | 'user:manage'
  | 'audit:read';

export const PERMISSION_MATRIX: Record<RoleType, PermissionAction[]> = {
  ADMIN: [
    'complaint:create', 'complaint:read', 'complaint:update', 'complaint:close',
    'workorder:create', 'workorder:read', 'workorder:update',
    'inspection:perform', 'verification:perform',
    'config:update', 'user:manage', 'audit:read',
  ],
  OFFICER: [
    'complaint:create', 'complaint:read', 'complaint:update', 'complaint:close',
    'workorder:create', 'workorder:read', 'workorder:update',
    'inspection:perform', 'verification:perform',
  ],
  SUPERVISOR: [
    'complaint:read', 'complaint:update',
    'workorder:read', 'workorder:update',
    'inspection:perform',
  ],
  DEPARTMENT_USER: [
    'complaint:create', 'complaint:read', 'complaint:close',
    'verification:perform',
  ],
  VENDOR: ['workorder:read', 'workorder:update'],
  TECHNICIAN: ['workorder:read', 'workorder:update'],
};

export const hasPermission = (role: RoleType | undefined, action: PermissionAction): boolean => {
  if (!role) return false;
  return (PERMISSION_MATRIX[role] || []).includes(action);
};

export const stripSecrets = <T extends Record<string, any>>(obj: T, blocked = ['password', 'password_hash', 'token', 'secret', 'api_key']): T => {
  const clone: Record<string, any> = { ...obj };
  Object.keys(clone).forEach(key => {
    if (blocked.some(b => key.toLowerCase().includes(b))) delete clone[key];
    else if (clone[key] && typeof clone[key] === 'object') clone[key] = stripSecrets(clone[key], blocked);
  });
  return clone as T;
};
